import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useDragControls, type PanInfo } from "motion/react";
import type { ReactNode } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side: "left" | "right" | "bottom";
  title: string;
  children: ReactNode;
  className?: string;
  /** wide — для документа: справа шире, снизу почти во весь экран и тянется только за ручку. */
  size?: "default" | "wide";
};

const ease = [0.22, 1, 0.36, 1] as const;

/** Выезжающая панель: ящик истории слева, файлы справа на планшете и снизу на телефоне. */
export function Sheet({ open, onOpenChange, side, title, children, className = "", size = "default" }: Props) {
  const wide = size === "wide";
  const drag = useDragControls();
  const off = side === "left" ? { x: "-104%" } : side === "right" ? { x: "104%" } : { y: "104%" };
  const on = side === "bottom" ? { y: 0 } : { x: 0 };
  const pos =
    side === "left"
      ? "inset-y-0 left-0 w-[300px] max-w-[86%]"
      : side === "right"
        ? `inset-y-3 right-3 ${wide ? "w-[640px]" : "w-[340px]"} max-w-[calc(100%-24px)] rounded-card`
        : `inset-x-0 bottom-0 ${wide ? "h-[94dvh]" : "h-[78dvh]"} rounded-t-[24px]`;
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 120 || info.velocity.y > 600) onOpenChange(false);
  };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-40 bg-scrim data-[state=closed]:pointer-events-none!"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </Dialog.Overlay>
            <Dialog.Content asChild forceMount aria-describedby={undefined}>
              <motion.div
                initial={off}
                animate={on}
                exit={off}
                transition={{ duration: 0.3, ease }}
                {...(side === "bottom"
                  ? {
                      drag: "y" as const,
                      dragConstraints: { top: 0, bottom: 0 },
                      dragElastic: { top: 0, bottom: 0.6 },
                      onDragEnd,
                      // В документе палец листает страницы: лист тянется только за ручку.
                      ...(wide ? { dragControls: drag, dragListener: false } : {}),
                    }
                  : {})}
                className={`fixed z-50 flex flex-col data-[state=closed]:pointer-events-none! overflow-hidden bg-surface shadow-floating outline-none ${pos} ${className}`}
              >
                <Dialog.Title className="sr-only">{title}</Dialog.Title>
                {side === "bottom" && (
                  <span
                    aria-hidden
                    onPointerDown={(e) => drag.start(e)}
                    className="flex h-5 flex-none touch-none items-start justify-center pt-2"
                  >
                    <span className="h-1 w-10 rounded-full bg-line" />
                  </span>
                )}
                {children}
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
